import { useState } from 'react';
import { X, UserPlus, Copy, Check, Send, Building2, FolderKanban, Link2, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

export const InviteToWorkspaceModal = ({ 
  isOpen, 
  onClose, 
  token, 
  workspace,
  hubs = [],
  onInvited 
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [targetType, setTargetType] = useState('workspace');
  const [selectedHubId, setSelectedHubId] = useState('');
  const [selectedHubAccess, setSelectedHubAccess] = useState([]);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastInvite, setLastInvite] = useState(null); // Store the last created invite
  const [copiedId, setCopiedId] = useState(null);

  const selectedHub = hubs.find(h => h.id === selectedHubId);
  const targetName = targetType === 'workspace' 
    ? workspace?.name 
    : selectedHub?.name || 'Select a hub';

  const toggleHubAccess = (hubId) => {
    setSelectedHubAccess(prev => 
      prev.includes(hubId) 
        ? prev.filter(id => id !== hubId)
        : [...prev, hubId]
    );
  };

  const selectAllHubs = () => {
    if (selectedHubAccess.length === hubs.length) {
      setSelectedHubAccess([]);
    } else {
      setSelectedHubAccess(hubs.map(h => h.id));
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (targetType === 'hub' && !selectedHubId) {
      setError('Please select a hub to invite to');
      return;
    }

    if (targetType === 'workspace' && hubs.length > 0 && selectedHubAccess.length === 0) {
      setError('Please select at least one hub for access');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email,
          role,
          target_type: targetType,
          target_id: targetType === 'workspace' ? workspace.id : selectedHubId,
          hub_access: targetType === 'workspace' ? selectedHubAccess : null,
          message: message.trim() || null
        })
      });

      const data = await response.json();

      if (response.ok) {
        const inviteLink = `${window.location.origin}${data.invite_link}`;
        
        setLastInvite({
          email,
          role,
          targetType,
          targetName,
          hubCount: selectedHubAccess.length,
          inviteLink,
          emailSent: data.email_status === 'sent'
        });
        
        // Auto-copy to clipboard (with graceful fallback)
        try {
          await navigator.clipboard.writeText(inviteLink);
          setCopiedId('auto');
          setTimeout(() => setCopiedId(null), 3000);
          
          if (data.email_status === 'sent') {
            toast.success(`Invitation email sent to ${email}`);
          } else {
            toast.success('Invite link copied to clipboard!', {
              description: 'Share this link with your teammate'
            });
          }
        } catch (clipboardErr) {
          // Clipboard access denied - show success without auto-copy
          if (data.email_status === 'sent') {
            toast.success(`Invitation email sent to ${email}`);
          } else {
            toast.success('Invite link created!', {
              description: 'Click Copy to copy the link'
            });
          }
        }
        
        setEmail('');
        setMessage('');
        setSelectedHubAccess([]);
        onInvited?.(data);
      } else {
        setError(data.detail || 'Failed to send invitation');
      }
    } catch (err) {
      setError('Failed to send invitation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyLink = async (link, id) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      toast.success('Link copied!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      // Fallback: select the text for manual copy
      toast.info('Press Ctrl+C to copy the link');
    }
  };

  const handleClose = () => {
    setEmail('');
    setError('');
    setMessage('');
    setLastInvite(null);
    setSelectedHubAccess([]);
    onClose();
  };

  const createAnother = () => {
    setLastInvite(null);
    setEmail('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="invite-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <UserPlus size={20} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Invite People
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            data-testid="close-invite-modal"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          
          {/* Success State - Show invite link prominently */}
          {lastInvite && (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Check className="text-green-600" size={20} />
                  <span className="font-medium text-green-800 dark:text-green-200">
                    Invitation Created!
                  </span>
                </div>
                
                <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                  {lastInvite.emailSent 
                    ? `Email sent to ${lastInvite.email}` 
                    : `Share this link with ${lastInvite.email}`}
                </p>

                {/* Prominent Link Box */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-green-300 dark:border-green-700 p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Link2 size={16} className="text-gray-500" />
                    <span className="text-xs font-medium text-gray-500 uppercase">Invite Link</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={lastInvite.inviteLink}
                      readOnly
                      className="flex-1 text-sm bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 truncate"
                      onClick={(e) => e.target.select()}
                    />
                    <Button
                      onClick={() => copyLink(lastInvite.inviteLink, 'main')}
                      className={cn(
                        "px-4 transition-all",
                        copiedId === 'main' || copiedId === 'auto'
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-blue-600 hover:bg-blue-700"
                      )}
                    >
                      {copiedId === 'main' || copiedId === 'auto' ? (
                        <>
                          <Check size={16} className="mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy size={16} className="mr-1" />
                          Copy
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                {/* Invite Details */}
                <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800 text-xs text-green-700 dark:text-green-300 space-y-1">
                  <div className="flex justify-between">
                    <span>Inviting to:</span>
                    <span className="font-medium">{lastInvite.targetName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Role:</span>
                    <span className="font-medium capitalize">{lastInvite.role}</span>
                  </div>
                  {lastInvite.hubCount > 0 && (
                    <div className="flex justify-between">
                      <span>Hub access:</span>
                      <span className="font-medium">{lastInvite.hubCount} hub(s)</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={createAnother}
                  className="flex-1"
                >
                  <UserPlus size={16} className="mr-2" />
                  Invite Another
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  className="flex-1"
                >
                  Done
                </Button>
              </div>
            </div>
          )}

          {/* Invite Form */}
          {!lastInvite && (
            <>
              {/* Target Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Invite to
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setTargetType('workspace'); setSelectedHubId(''); }}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 transition-all",
                      targetType === 'workspace'
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300"
                        : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300"
                    )}
                    data-testid="invite-to-workspace"
                  >
                    <Building2 size={16} />
                    <span className="text-sm font-medium">Workspace</span>
                  </button>
                  <button
                    onClick={() => { setTargetType('hub'); setSelectedHubAccess([]); }}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border-2 transition-all",
                      targetType === 'hub'
                        ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                        : "border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300"
                    )}
                    data-testid="invite-to-hub"
                  >
                    <FolderKanban size={16} />
                    <span className="text-sm font-medium">Hub Only</span>
                  </button>
                </div>
              </div>

              {/* Hub Selection (if hub target) */}
              {targetType === 'hub' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select Hub
                  </label>
                  <select
                    value={selectedHubId}
                    onChange={(e) => setSelectedHubId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    data-testid="hub-select"
                  >
                    <option value="">Choose a hub...</option>
                    {hubs.map(hub => (
                      <option key={hub.id} value={hub.id}>{hub.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Hub Access Selection (if workspace target and hubs exist) */}
              {targetType === 'workspace' && hubs.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Hub Access
                    </label>
                    <button
                      onClick={selectAllHubs}
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400"
                    >
                      {selectedHubAccess.length === hubs.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2">
                    {hubs.map(hub => (
                      <label 
                        key={hub.id}
                        className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedHubAccess.includes(hub.id)}
                          onChange={() => toggleHubAccess(hub.id)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <FolderKanban size={14} className="text-gray-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{hub.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">
                    {selectedHubAccess.length} of {hubs.length} hubs selected
                  </p>
                </div>
              )}

              {/* No hubs message for workspace invite */}
              {targetType === 'workspace' && hubs.length === 0 && (
                <div className="px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    No hubs yet. Create a hub first to grant access.
                  </p>
                </div>
              )}

              {/* Email Input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="colleague@company.com"
                  className="w-full"
                  data-testid="invite-email-input"
                />
              </div>

              {/* Role Selection */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  data-testid="role-select"
                >
                  <option value="admin">Admin - Full access</option>
                  <option value="member">Member - Can view and edit</option>
                  <option value="guest">Guest - View only</option>
                </select>
              </div>

              {/* Optional Message */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Personal Message (optional)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Hey! Join our team workspace..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                />
              </div>

              {/* Error */}
              {error && (
                <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  <strong>How it works:</strong> An invite link will be created and automatically copied to your clipboard. Share it with your teammate to invite them.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer - Only show when not in success state */}
        {!lastInvite && (
          <div className="flex gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" onClick={handleClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleInvite} 
              disabled={isLoading || !email}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="send-invite-btn"
            >
              {isLoading ? (
                'Creating...'
              ) : (
                <>
                  <Link2 size={16} className="mr-2" />
                  Create Invite Link
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteToWorkspaceModal;
